import { useState } from 'react';
import { Typography, Select, message } from 'antd';
import { SaveOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { businessApi } from '../../api/businessApi';
import { useBusinessStore } from '../../store/businessStore';
import { telegram } from '../../utils/telegram';
import SettingsInfoButton from './SettingsInfoButton';

const SLOT_STEP_VALUES = [5, 10, 15, 20, 30, 60, 120] as const;

export default function BookingSettingsSection() {
  const { t } = useTranslation(['common', 'owner']);
  const { selectedBusiness, updateSelectedBusiness } = useBusinessStore();

  const [slotStep, setSlotStep] = useState(selectedBusiness?.slotStepMinutes || 15);
  const [defaultDuration, setDefaultDuration] = useState(selectedBusiness?.defaultServiceDurationMinutes || 30);
  const [saving, setSaving] = useState(false);

  if (!selectedBusiness) return null;

  const hasChanges =
    slotStep !== selectedBusiness.slotStepMinutes ||
    defaultDuration !== selectedBusiness.defaultServiceDurationMinutes;

  const slotStepOptions = SLOT_STEP_VALUES.map((value) => ({
    value,
    label: t('common:units.minutes', { count: value }),
  }));

  const durationOptions = Array.from({ length: Math.floor(480 / slotStep) }, (_, i) => {
    const val = (i + 1) * slotStep;
    return { value: val, label: t('common:units.minutes', { count: val }) };
  });

  const handleSlotStepChange = (val: number) => {
    setSlotStep(val);
    const remainder = defaultDuration % val;
    if (remainder !== 0) {
      const rounded = Math.max(val, Math.round(defaultDuration / val) * val);
      setDefaultDuration(rounded > 480 ? val : rounded);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await businessApi.updateSettings(selectedBusiness.id, {
        slotStepMinutes: slotStep,
        defaultServiceDurationMinutes: defaultDuration,
      });
      updateSelectedBusiness(updated);
      telegram.hapticFeedback.notification('success');
      void message.success(t('common:status.saved'));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      void message.error(error?.response?.data?.message ?? t('common:errors.saveFailed'));
      telegram.hapticFeedback.notification('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ borderRadius: 16, background: 'var(--bl-surface, #fff)', border: '1px solid var(--bl-divider, #ebebeb)', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '12px 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderBottom: '1px solid var(--bl-divider, #ebebeb)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#7c3aed' }}>
            <SettingOutlined />
          </div>
          <Typography.Text style={{ fontSize: 15, fontWeight: 700, color: 'var(--bl-text-strong, #0f172a)' }}>
            {t('owner:settings.bookingSettings.title')}
          </Typography.Text>
        </div>
        <SettingsInfoButton
          title={t('owner:settings.bookingSettings.info.title')}
          body={t('owner:settings.bookingSettings.info.body')}
        />
      </div>

      <div style={{ padding: '14px 14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Typography.Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #6b7280)' }}>
          {t('owner:settings.bookingSettings.hint')}
        </Typography.Text>

        {/* Slot step */}
        <div>
          <Typography.Text style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--bl-text-muted, #6b7280)', marginBottom: 6 }}>
            {t('owner:settings.bookingSettings.slotStepLabel')}
          </Typography.Text>
          <Select value={slotStep} onChange={handleSlotStepChange} style={{ width: '100%' }} options={slotStepOptions} />
        </div>

        {/* Default duration */}
        <div>
          <Typography.Text style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--bl-text-muted, #6b7280)', marginBottom: 6 }}>
            {t('owner:settings.bookingSettings.defaultDurationLabel')}
          </Typography.Text>
          <Select value={defaultDuration} onChange={setDefaultDuration} style={{ width: '100%' }} options={durationOptions} />
        </div>

        {hasChanges && (
          <button type="button" onClick={() => void handleSave()} disabled={saving} style={{ width: '100%', height: 46, borderRadius: 12, background: saving ? '#f5f3ff' : '#7c3aed', border: 'none', color: saving ? '#7c3aed' : '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: saving ? 0.7 : 1 }}>
            <SaveOutlined /> {saving ? t('common:actions.saving') : t('common:actions.save')}
          </button>
        )}
      </div>
    </div>
  );
}
